import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useRouter } from "next/navigation";
import posthog from "posthog-js";
import { useState } from "react";
import { useForm } from "react-hook-form";
import type { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { useGenerateQuestionsText } from "@/features/questions/api/use-generate-questions-text";
import { useCreateStudyset } from "@/features/studysets/api/use-create-studyset";
import { useUserPlanQuery } from "@/features/users/api/user-plan-options";
import { useFreeUploadLimit } from "@/lib/hooks/use-free-upload-limit";
import usePricingModal, { PAYWALL_TYPES } from "@/lib/hooks/use-pricing-modal";
import { pastedTextSchema } from "@/lib/types";
import { useI18n } from "@/locales/client";

interface PasteTextFormProps {
  id?: string;
  cancel: () => void;
  cancelText: string;
  handleComplete: () => void;
}

export const PasteTextForm = ({
  id,
  cancel,
  cancelText,
  handleComplete,
}: PasteTextFormProps) => {
  const { data, status } = useUserPlanQuery();
  const t = useI18n();
  const freeUploadLimit = useFreeUploadLimit();

  const form = useForm({
    resolver: zodResolver(pastedTextSchema),
    defaultValues: {
      pastedText: "",
    },
  });

  const [charCount, setCharCount] = useState(0);
  const { onOpen: onOpenPricing } = usePricingModal();
  const router = useRouter();
  const createStudySetMutation = useCreateStudyset();

  const generateQuestionMutation = useGenerateQuestionsText();
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    setCharCount(text.length);
    form.setValue("pastedText", text);
  };

  if (status === "pending") {
    return (
      <div className="flex flex-col items-center justify-center pt-5 pb-6">
        <div className="flex items-center justify-center gap-2">
          <Loader2 className="animate-spin text-blue-500 size-5" />
          <p className="text-sm text-[#09090B]">{t("loading")}</p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <p className="text-sm font-medium text-destructive">
        {t("newStudySetDrawer.errors.generic")}
      </p>
    );
  }

  const hasReachedLimit =
    !data.isSubscribed && data.generationCount >= freeUploadLimit;

  async function onSubmit(values: z.infer<typeof pastedTextSchema>) {
    if (hasReachedLimit) {
      onOpenPricing({
        upgradeReason: t("newStudySetDrawer.upgradeReason"),
        description: t("newStudySetDrawer.upgradeDescription"),
        paywallType: PAYWALL_TYPES.PASTE_TEXT,
      });
      posthog.capture("paywall_shown", {
        userId: data?.userId,
        paywallType: PAYWALL_TYPES.PASTE_TEXT,
        generationCount: data?.generationCount,
      });
      cancel();
      return;
    }

    try {
      let studySetId = id;

      if (!studySetId) {
        const newStudySet = await createStudySetMutation.mutateAsync({
          name: t("newStudySetDrawer.untitledStudySet"),
        });
        studySetId = newStudySet.id;

        posthog.capture("study_set_created", {
          studySetId: newStudySet.id,
          userId: newStudySet.userId,
          creationMethod: "paste_text",
        });
      }

      await generateQuestionMutation.mutateAsync({
        id: studySetId,
        pastedText: values.pastedText,
      });

      if (!id) {
        router.push(`/studysets/${studySetId}/loading`);
      }
      handleComplete();
    } catch (_error) {
      // toast and log error handled in useGenerateQuestionsText mutation
    }
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-6"
      >
        <FormField
          control={form.control}
          name="pastedText"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <div className="relative">
                  <Textarea
                    disabled={generateQuestionMutation.isPending}
                    onKeyDown={async (e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        form.handleSubmit(onSubmit)();
                      }
                    }}
                    minRows={10}
                    maxRows={10}
                    rows={10}
                    className="resize-none w-full"
                    placeholder={t("newStudySetDrawer.pasteTextPlaceholder")}
                    {...field}
                    onChange={handleTextChange}
                  />
                  <div className="absolute bottom-2 right-2 text-sm text-muted-foreground">
                    {charCount}
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex flex-col-reverse gap-y-4 md:gap-y-0 md:flex-row w-full md:items-center md:justify-between">
          <Button
            disabled={generateQuestionMutation.isPending}
            onClick={cancel}
            type="button"
            variant="ghost"
          >
            {cancelText}
          </Button>

          <Button disabled={generateQuestionMutation.isPending} type="submit">
            {generateQuestionMutation.isPending ? (
              <div className="flex items-center gap-2">
                <Loader2 className="animate-spin size-4" />{" "}
                <span>
                  {cancelText !== "Go Back"
                    ? t("newStudySetDrawer.generatingQuestions")
                    : t("newStudySetDrawer.generatingStudySet")}
                </span>
              </div>
            ) : (
              <span>
                {cancelText !== "Go Back"
                  ? t("newStudySetDrawer.generateQuestions")
                  : t("newStudySetDrawer.generateStudySet")}
              </span>
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};
